import React, { useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

// Two-step in-page flow (no separate reset-link page needed): request a
// code, then enter the code + new password on the same screen. Convex
// Auth's Password provider is code-based (see ResendOTPPasswordReset.ts),
// not magic-link-based, so there's no separate URL for a user to land on.
export default function ForgotPasswordPage() {
  useDocumentTitle('Forgot Password');
  const [step, setStep] = useState<'request' | 'confirm'>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { resetPassword, confirmPasswordReset } = useAuth();

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await resetPassword(email);
      setStep('confirm');
    } catch (err: any) {
      setError(err.message || 'Failed to send reset code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const { error: confirmError } = await confirmPasswordReset(email, code, newPassword);
      if (confirmError) {
        setError(confirmError.message);
        return;
      }
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper-50 p-4">
      <div className="w-full max-w-md bg-white border border-paper-300 p-6">
        <div className="mb-6 space-y-1 text-center">
          <h1 className="font-serif text-4xl font-bold text-navy-900">Reset your password</h1>
          <p className="text-sm text-paper-500">
            {step === 'request'
              ? "Enter your email and we'll send you a reset code"
              : `Enter the code sent to ${email}`}
          </p>
        </div>

        {success ? (
          <div className="space-y-4">
            <div className="py-4">
              <p className="text-center text-sm text-navy-600">
                Your password has been reset. You can sign in with your new password now.
              </p>
            </div>
            <a
              href="/login"
              className="block w-full bg-navy-900 hover:bg-navy-800 text-white font-medium px-5 py-3 text-center"
            >
              Back to sign in
            </a>
          </div>
        ) : step === 'request' ? (
          <form onSubmit={handleRequestCode} className="space-y-4">
            {error && <div className="bg-red-50 p-3 text-sm text-red-600">{error}</div>}
            <div className="space-y-2">
              <label htmlFor="email" className="text-sm font-medium">
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="name@lawfirm.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-paper-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest-500"
                required
              />
            </div>
            <button
              type="submit"
              className="w-full bg-navy-900 hover:bg-navy-800 text-white font-medium px-5 py-3"
              disabled={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send reset code'}
            </button>
            <div className="text-center">
              <a href="/login" className="text-sm text-forest-500 hover:text-forest-600 hover:underline">
                Back to sign in
              </a>
            </div>
          </form>
        ) : (
          <form onSubmit={handleConfirm} className="space-y-4">
            {error && <div className="bg-red-50 p-3 text-sm text-red-600">{error}</div>}
            <div className="space-y-2">
              <label htmlFor="code" className="text-sm font-medium">
                Reset code
              </label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full border border-paper-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest-500"
                required
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="newPassword" className="text-sm font-medium">
                New password
              </label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border border-paper-300 bg-white px-3 py-2 text-sm focus:outline-none focus:border-forest-500"
                required
                minLength={8}
              />
            </div>
            <button
              type="submit"
              className="w-full bg-navy-900 hover:bg-navy-800 text-white font-medium px-5 py-3"
              disabled={isLoading}
            >
              {isLoading ? 'Resetting...' : 'Reset password'}
            </button>
            <div className="text-center">
              <button
                type="button"
                onClick={() => setStep('request')}
                className="text-sm text-forest-500 hover:text-forest-600 hover:underline"
              >
                Didn't get a code? Try again
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
